import React, { useState, useEffect } from "react";
import "./ModalStockRetirada.scss";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import InputNumber from "../../../shared/components/inputs/InputNumber/InputNumber";
import { useLoading } from "../../../shared/context/LoadingContext";
import { formatarDataGuardada } from "../../../shared/util/datasGuardadas";
import moment from "moment";
import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";
import { Info, Package, CalendarDays, Building2, MapPin } from "lucide-react";
import NotaInformativa from "../../../shared/components/notaInformativa/NotaInformativa";

function ModalStockRetirada({
  handleCloseModalStockRetirada,
  stock,
  sendRequest,
  open,
  atualizarStock,
  dadosProdutoFiltrado,
  auth,
  clinica,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quantidade, setQuantidade] = useState(1);
  const [responsaveisMovimento, setResponsaveisMovimento] = useState([]);
  const [responsavelMovimento, setResponsavelMovimento] = useState(auth.userId);
  const [beneficiario, setBeneficiario] = useState(auth.userId);

  const stockRetirada = dadosProdutoFiltrado.selecionado;
  const nomeLote = dadosProdutoFiltrado.nomeLote;
  const localizacaoOrigemId = dadosProdutoFiltrado.localizacaoOrigemId;

  const selectedLoteInfo =
    stockRetirada?.lotes?.find((l) => l.lote === nomeLote) || {};

  const primeiraLocComStock =
    selectedLoteInfo.localizacoes?.find((l) => l.quantidade > 0)?.localizacao
      ?._id || null;

  const [localizacaoSelecionadaId, setLocalizacaoSelecionadaId] = useState(
    primeiraLocComStock || localizacaoOrigemId || "",
  );

  const localizacaoOrigem = selectedLoteInfo.localizacoes?.find(
    (l) =>
      l.localizacao?._id?.toString() === localizacaoSelecionadaId?.toString(),
  );

  const quantidadeDisponivel = localizacaoOrigem?.quantidade || 0;

  const localizacoesDisponiveis =
    selectedLoteInfo.localizacoes?.filter(
      (l) => !l.esgotado && l.quantidade > 0,
    ) || [];

  useEffect(() => {
    fetchFuncionariosAndUser();

    // Definir quantidade inicial como 1 ou o máximo disponível no lote se for menor que 1
    if (selectedLoteInfo.quantidade) {
      setQuantidade(Math.min(1, selectedLoteInfo.quantidade));
    }
  }, [sendRequest, clinica.clinica._id, auth.token, stockRetirada]);

  const fetchFuncionariosAndUser = async () => {
    startLoading();
    try {
      let grabbers = [];

      let responseData;
      try {
        responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/funcionarios/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        );
      } catch (err) {
        console.error("err", err);
      }

      responseData.funcionarios.forEach((fun) => {
        let fullName = `${fun.nome}${fun.cargo ? ` - ${fun.cargo}` : ""}`;
        if (fullName.endsWith(" - undefined")) {
          fullName = fun.nome;
        }

        grabbers.push({
          id: fun.id,
          nome: `${fun.nome}${fun.cargo ? ` - ${fun.cargo}` : ""}`,
          type: "Funcionario",
        });
      });

      let responseData2;
      try {
        responseData2 = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/users/ativos/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          },
        );
      } catch (err) {
        console.error("err", err);
      }

      responseData2.users.forEach((user) => {
        let fullName = `${user.name}${
          user.profissao ? ` - ${user.profissao}` : ""
        }`;
        if (fullName.endsWith(" - undefined")) {
          fullName = user.name;
        }

        grabbers.push({
          id: user.id,
          nome: fullName,
          type: "User",
        });
      });

      setResponsaveisMovimento(grabbers);
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
  };

  const changeResponsavelMovimento = (e) => {
    setResponsavelMovimento(e.target.value);
  };

  const changeQuantidade = (value) => {
    setQuantidade(value);
  };

  const getMaxQuantityForSelectedLote = () => {
    return quantidadeDisponivel;
  };

  const submit = async () => {
    if (!localizacaoSelecionadaId) {
      toast.error("Por favor selecione uma localização.");
      return;
    }

    startLoading();
    try {
      const stockItem = stock.stock.find(
        (st) => st.produto._id === stockRetirada.produto._id,
      );
      const loteIndex = stockItem.lotes.findIndex((l) => l.lote === nomeLote);

      if (loteIndex === -1) {
        throw new Error("Lote não encontrado");
      }

      const selectedBeneficiario = responsaveisMovimento.find(
        (r) => r.id === beneficiario,
      );

      const selectedResponsavel = responsaveisMovimento.find(
        (r) => r.id === responsavelMovimento,
      );

      // Só a intenção: o que retirar, de onde e quanto. As quantidades novas, o
      // preço, o fornecedor e a validade são calculados/lidos no servidor, que
      // valida a disponibilidade antes de descontar.
      const retirada = {
        produto: stockItem.produto._id,
        lote: nomeLote,
        localizacaoId: localizacaoSelecionadaId,
        quantidade: Number(quantidade),
        beneficiario: selectedBeneficiario
          ? {
              tipo: selectedBeneficiario.type,
              responsavel: selectedBeneficiario.id,
            }
          : null,
        responsavelMovimento: selectedResponsavel
          ? {
              tipo: selectedResponsavel.type,
              responsavel: selectedResponsavel.id,
            }
          : null,
      };

      const formData = new FormData();
      formData.append("retirada", JSON.stringify(retirada));

      const stockReturned = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/fazerretirada/${stock._id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      atualizarStock(
        stockReturned.stock,
        `Retirada efetuada com sucesso!${
          selectedLoteInfo.quantidade === quantidade ? "Lote esgotado!" : ""
        }`,
      );
    } catch (err) {
      console.error("err", err);
    } finally {
      setIsSubmitting(false);
      stopLoading();
      handleCloseModalStockRetirada();
    }
  };

  const preSubmitHandler = () => {
    if (!isSubmitting) {
      setIsSubmitting(true);
      submit();
    }
  };

  const changeBeneficiario = (e) => {
    setBeneficiario(e.target.value);
  };

  const loteHasFornecedor =
    selectedLoteInfo.fornecedor && selectedLoteInfo.fornecedor.nome;
  const loteHasPreco =
    selectedLoteInfo.precoCompra && selectedLoteInfo.precoCompra > 0;

  return (
    <Modal
      open={open}
      onClose={handleCloseModalStockRetirada}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        className="fluxo-atendimento__modal__box retirarDoStock__modal"
        id="modal__cinquentaPorCento"
      >
        <h1 className="fluxo-atendimento__modal__titulo">
          Retirar produto do stock
        </h1>

        {/* Dois campos parecidos e fáceis de trocar. Trocados, o consumo fica
            atribuído a quem foi buscar o material em vez de a quem o usou — e
            os relatórios de consumo por profissional deixam de querer dizer
            alguma coisa. */}
        <NotaInformativa style={{ margin: "0 0 16px" }}>
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            <li>
              <strong>Beneficiário</strong> — quem fica com o produto ou quem
              o usa.
            </li>
            <li>
              <strong>Movimentado por</strong> — quem está a fazer esta
              retirada.
            </li>
            <li>
              Para material gasto no consultório, ponha como beneficiário o{" "}
              <strong>dentista que o vai usar</strong>. É assim que o consumo
              fica ligado a quem atende.
            </li>
          </ul>
        </NotaInformativa>

        <div className="retirarDoStock__container">
          <div className="retirarDoStock__container__header">
            <span className="retirarDoStock__container__nomeProduto">
              {stockRetirada.produto.nome}
              {stockRetirada.produto.classificacao === "MP" && (
                <span className="retirarDoStock__container__classificacao">
                  (Matéria Prima)
                </span>
              )}
            </span>

            {/* Mostrar informações de armazém e localização */}
            <div className="retirarDoStock__container__localizacao">
              <Tooltip title="Localização física do produto">
                <Info
                  className="retirarDoStock__container__info-icon"
                  size={16}
                />
              </Tooltip>
              <span>
                Armazém:{" "}
                <strong>
                  {localizacaoOrigem?.localizacao?.armazem?.codigo || "N/A"}
                </strong>
                , Localização:{" "}
                <strong>
                  {localizacaoOrigem?.localizacao?.codigo || "N/A"} —{" "}
                  {localizacaoOrigem?.localizacao?.nome || "Principal"}
                </strong>{" "}
                ({quantidadeDisponivel} disponíveis)
              </span>
            </div>
          </div>

          {/* Informações do lote selecionado */}
          <div className="retirarDoStock__container__lote-info">
            <div className="retirarDoStock__container__lote-header">
              <Package
                className="retirarDoStock__container__lote-icon"
                size={18}
              />
              <h3>
                Lote Selecionado: <strong>{nomeLote}</strong>
              </h3>
            </div>

            <div className="retirarDoStock__container__lote-details">
              <div className="retirarDoStock__container__lote-detail">
                <span className="retirarDoStock__container__info-label">
                  Quantidade Disponível:
                </span>
                <span className="retirarDoStock__container__info-value">
                  {selectedLoteInfo.quantidade || 0}
                </span>
              </div>

              {selectedLoteInfo.dataValidade && (
                <div className="retirarDoStock__container__lote-detail">
                  <CalendarDays
                    className="retirarDoStock__container__detail-icon"
                    size={16}
                  />
                  <span className="retirarDoStock__container__info-label">
                    Validade:
                  </span>
                  <span className="retirarDoStock__container__info-value">
                    {formatarDataGuardada(selectedLoteInfo.dataValidade)}
                  </span>
                </div>
              )}

              {loteHasFornecedor && (
                <div className="retirarDoStock__container__lote-detail">
                  <Building2
                    className="retirarDoStock__container__detail-icon"
                    size={16}
                  />
                  <span className="retirarDoStock__container__info-label">
                    Fornecedor:
                  </span>
                  <span className="retirarDoStock__container__info-value">
                    {selectedLoteInfo.fornecedor.nome}
                  </span>
                </div>
              )}

              {loteHasPreco && (
                <div className="retirarDoStock__container__lote-detail">
                  <span className="retirarDoStock__container__info-label">
                    Preço de Compra:
                  </span>
                  <span className="retirarDoStock__container__info-value">
                    {selectedLoteInfo.precoCompra.toFixed(2)} CVE
                  </span>
                </div>
              )}

              {selectedLoteInfo.autoGerado && (
                <div className="retirarDoStock__container__lote-detail">
                  <span className="retirarDoStock__container__lote-autogenerated">
                    Lote gerado automaticamente
                  </span>
                </div>
              )}
            </div>
          </div>

          {localizacoesDisponiveis.length > 1 && (
            <div
              className="retirarDoStock__container__lote-info"
              style={{ marginBottom: 12 }}
            >
              <div className="retirarDoStock__container__lote-header">
                <MapPin
                  size={18}
                  className="retirarDoStock__container__lote-icon"
                />
                <h3>Localização de Origem</h3>
              </div>
              <div className="customInputContainer">
                <select
                  className="customInputSelect"
                  value={localizacaoSelecionadaId}
                  onChange={(e) => {
                    setLocalizacaoSelecionadaId(e.target.value);
                    setQuantidade(1);
                  }}
                >
                  {localizacoesDisponiveis.map((l) => (
                    <option key={l.localizacao._id} value={l.localizacao._id}>
                      {l.localizacao.armazem?.codigo || "N/A"} /{" "}
                      {l.localizacao.codigo} — {l.localizacao.nome} (
                      {l.quantidade} disp.)
                    </option>
                  ))}
                </select>
                <label className="customInputLabelSelect">Localização</label>
              </div>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr 2fr",
              gap: "15px",
              marginBottom: "10px",
            }}
          >
            <InputNumber
              initialValue={1}
              label="Qtd a Retirar"
              handleChange={changeQuantidade}
              min={1}
              max={getMaxQuantityForSelectedLote()}
              notEmpty={true}
            />

            <div className="customInputContainer">
              <select
                onChange={changeBeneficiario}
                className="customInputSelect"
                value={beneficiario}
              >
                {responsaveisMovimento.map((item, index) => (
                  <option key={index} value={item.id}>
                    {item.nome}
                  </option>
                ))}
              </select>
              <label className="customInputLabelSelect">Beneficiário</label>
            </div>

            <div className="customInputContainer">
              <select
                onChange={changeResponsavelMovimento}
                className="customInputSelect"
                value={responsavelMovimento}
              >
                {responsaveisMovimento &&
                  responsavelMovimento &&
                  responsaveisMovimento.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.nome}
                    </option>
                  ))}
              </select>
              <label className="customInputLabelSelect">Movimentado por*</label>
            </div>
          </div>

          <div className="fluxo-atendimento__modal__bottom_btns">
            <span
              className="cancel-btn"
              onClick={handleCloseModalStockRetirada}
              style={{ display: "block" }}
            >
              Fechar
            </span>

            <span
              disabled={isSubmitting}
              className="blue-button"
              onClick={preSubmitHandler}
            >
              {isSubmitting ? "Guardando..." : "Guardar"}
            </span>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default ModalStockRetirada;
